import React, { useState } from 'react';
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link,
  InputAdornment,
  IconButton,
  Fade,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import ForgotPasswordModal from './ForgotPasswordModal';

const SignIn = ({ onSwitchToSignUp }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  
  const { signin } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signin(formData.email, formData.password);
      navigate('/dashboard');
    } catch (error) {
      console.error('Auth error:', error);
      setError(t('auth.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #212121 0%, #424242 100%)',
        display: 'flex',
        alignItems: 'center',
        py: { xs: 2, md: 4 }
      }}
    >
      <Container maxWidth="sm">
        <Fade in={true} timeout={600}>
          <Box
            sx={{
              backgroundColor: '#2a2a2a',
              borderRadius: 4,
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1, p: { xs: 3, md: 6 } }}>
              {/* Header */}
              <Box textAlign="center" mb={{ xs: 3, md: 5 }}>
                <Box display="flex" justifyContent="center" mb={{ xs: 2, md: 3 }}>
                  <Box
                    sx={{
                      width: { xs: 60, md: 80 },
                      height: { xs: 60, md: 80 },
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #f5f5dc 0%, #e8e8c8 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: { xs: 1, md: 2 },
                      p: { xs: 1, md: 1.5 },
                      animation: 'pulse 2s infinite',
                      '@keyframes pulse': {
                        '0%': {
                          boxShadow: '0 0 0 0 rgba(245, 245, 220, 0.7)'
                        },
                        '70%': {
                          boxShadow: '0 0 0 10px rgba(245, 245, 220, 0)'
                        },
                        '100%': {
                          boxShadow: '0 0 0 0 rgba(245, 245, 220, 0)'
                        }
                      }
                    }}
                  >
                    <img
                      src="/Logos/logoFocus.png"
                      alt="BookingBarber"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  </Box>
                </Box>
                
                <Typography
                  variant={isMobile ? "h4" : "h3"}
                  component="h1"
                  gutterBottom
                  sx={{
                    fontWeight: 800,
                    background: 'linear-gradient(135deg, #f5f5dc 0%, #e8e8c8 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: { xs: 1, md: 2 }
                  }}
                >
                  {t('auth.signInTitle')}
                </Typography>
                
                <Typography
                  variant={isMobile ? "body1" : "h6"}
                  sx={{
                    mb: { xs: 2, md: 3 },
                    fontWeight: 400,
                    lineHeight: 1.6,
                    color: 'rgba(255,255,255,0.8)'
                  }}
                >
                  {t('auth.accessYourSpace')} <strong style={{ color: '#f5f5dc' }}>BookingBarber</strong>
                </Typography>
              </Box>

              {/* Error Alert */}
              {error && (
                <Fade in={true} timeout={300}>
                  <Alert
                    severity="error"
                    sx={{
                      mb: { xs: 2, md: 3 },
                      backgroundColor: 'rgba(239, 68, 68, 0.1)',
                      color: '#ef4444',
                      borderRadius: 2,
                      border: '1px solid rgba(239, 68, 68, 0.2)',
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      '& .MuiAlert-icon': {
                        color: '#ef4444'
                      }
                    }}
                  >
                    {error}
                  </Alert>
                </Fade>
              )}

              {/* Form */}
              <Box component="form" onSubmit={handleSubmit}>
                <Box sx={{ mb: { xs: 2, md: 3 } }}>
                  <TextField
                    fullWidth
                    label={t('auth.emailLabel')}
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon sx={{ color: '#f5f5dc' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        borderRadius: 2,
                        '& fieldset': {
                          borderColor: 'rgba(255,255,255,0.2)',
                          borderWidth: 2
                        },
                        '&:hover fieldset': {
                          borderColor: '#f5f5dc'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#f5f5dc'
                        },
                        '& input': {
                          color: 'white',
                          fontSize: { xs: '1rem', md: '1.1rem' }
                        }
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255,255,255,0.7)',
                        fontSize: { xs: '1rem', md: '1.1rem' },
                        '&.Mui-focused': {
                          color: '#f5f5dc'
                        }
                      }
                    }}
                  />
                </Box>
                
                <Box sx={{ mb: { xs: 3, md: 4 } }}>
                  <TextField
                    fullWidth
                    label={t('auth.passwordLabel')}
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: '#f5f5dc' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            sx={{ color: 'rgba(255,255,255,0.7)' }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        borderRadius: 2,
                        '& fieldset': {
                          borderColor: 'rgba(255,255,255,0.2)',
                          borderWidth: 2
                        },
                        '&:hover fieldset': {
                          borderColor: '#f5f5dc'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#f5f5dc'
                        },
                        '& input': {
                          color: 'white',
                          fontSize: { xs: '1rem', md: '1.1rem' }
                        }
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255,255,255,0.7)',
                        fontSize: { xs: '1rem', md: '1.1rem' },
                        '&.Mui-focused': {
                          color: '#f5f5dc'
                        }
                      }
                    }}
                  />
                </Box>

                {/* Sign In Button */}
                <Box sx={{ mb: { xs: 3, md: 4 } }}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading}
                    endIcon={!loading && <ArrowForwardIcon />}
                    sx={{
                      py: { xs: 1.5, md: 2 },
                      borderRadius: 3,
                      fontSize: { xs: '1rem', md: '1.1rem' },
                      fontWeight: 600,
                      background: 'linear-gradient(135deg, #f5f5dc 0%, #e8e8c8 100%)',
                      color: '#212121',
                      boxShadow: '0 8px 25px rgba(245, 245, 220, 0.3)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #e8e8c8 0%, #dcdcb8 100%)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 12px 35px rgba(245, 245, 220, 0.4)'
                      },
                      '&:disabled': {
                        background: 'rgba(255,255,255,0.1)',
                        color: 'rgba(255,255,255,0.3)',
                        transform: 'none',
                        boxShadow: 'none'
                      }
                    }}
                  >
                    {loading ? t('auth.signingIn') : t('auth.signInButton')}
                  </Button>
                </Box>

                {/* Links */}
                <Box textAlign="center" sx={{ mb: { xs: 2, md: 3 } }}>
                  <Typography variant="body2" sx={{ mb: 1.5, color: 'rgba(255,255,255,0.7)' }}>
                    <Link
                      component="button"
                      type="button"
                      onClick={() => setForgotPasswordOpen(true)}
                      sx={{
                        textDecoration: 'none',
                        fontWeight: 600,
                        color: '#f5f5dc',
                        transition: 'all 0.3s ease',
                        fontSize: { xs: '0.875rem', md: '1rem' },
                        '&:hover': {
                          textDecoration: 'underline',
                          color: '#e8e8c8'
                        }
                      }}
                    >
                      {t('auth.forgotPassword')}
                    </Link>
                  </Typography>
                  
                  <Typography variant="body2" sx={{ mb: 2, color: 'rgba(255,255,255,0.7)' }}>
                    {t('auth.noAccount')}{' '}
                    <Link
                      component="button"
                      type="button"
                      onClick={onSwitchToSignUp}
                      sx={{
                        textDecoration: 'none',
                        fontWeight: 600,
                        color: '#f5f5dc',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          textDecoration: 'underline',
                          color: '#e8e8c8'
                        }
                      }}
                    >
                      {t('auth.signUpLink')}
                    </Link>
                  </Typography>
                </Box>

                {/* Back to Home Button */}
                <Box display="flex" justifyContent="center">
                  <Button
                    variant="text"
                    onClick={() => navigate('/')}
                    startIcon={<ArrowBackIcon />}
                    sx={{
                      color: 'rgba(255,255,255,0.6)',
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      fontWeight: 500,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        color: '#f5f5dc',
                        backgroundColor: 'rgba(245,245,220,0.1)'
                      }
                    }}
                  >
                    {t('auth.backToHome')}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </Fade>

        {/* Forgot Password Modal */}
        <ForgotPasswordModal
          open={forgotPasswordOpen}
          onClose={() => setForgotPasswordOpen(false)}
        />
      </Container>
    </Box>
  );
};

export default SignIn;